import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Pagination } from '@mui/material';
import classNames from 'classnames';

import Loadable from '@components/Loadable/Loadable';
import { HighRiskSupplement } from '@models/highRiskSupplement';
import { highRiskSupplementsDetailsIdSelector } from '@store/features/highRiskSupplementDetails/selectors';
import {
  highRiskSupplementsListErrorSelector,
  highRiskSupplementsListIsLoadingSelector,
  highRiskSupplementsListPaginationSelector,
  highRiskSupplementsListSelector,
} from '@store/features/highRiskSupplements/selectors';

import HRSItemCard from '../HRSItemCard/HRSItemCard';
import HRSListEmptyCard from '../HRSListEmptyCard/HRSListEmptyCard';

interface IProps {
  onFetchEntriesList: () => void;
  onEntryClick: (data: HighRiskSupplement) => void;
  onPaginationChange: (page: number) => void;
}

const HRSFullList: FC<IProps> = ({
  onFetchEntriesList,
  onEntryClick,
  onPaginationChange,
}): JSX.Element => {
  const { t } = useTranslation();
  const riskSupplementsListIsLoading = useSelector(highRiskSupplementsListIsLoadingSelector);
  const riskSupplementsListError = useSelector(highRiskSupplementsListErrorSelector);
  const riskSupplementsList = useSelector(highRiskSupplementsListSelector);
  const listPagination = useSelector(highRiskSupplementsListPaginationSelector);
  const activeEntryId = useSelector(highRiskSupplementsDetailsIdSelector);

  const handleSearchPagination = useCallback(
    (event: React.SyntheticEvent<unknown>, page: number) => {
      onPaginationChange(page);
    },
    [onPaginationChange],
  );

  return (
    <>
      <Box
        className={classNames('high-risk-supplements__list-panel', {
          'high-risk-supplements__list-panel--empty': !(
            riskSupplementsList && riskSupplementsList.length
          ),
        })}
      >
        <Box>
          <Loadable
            isLoading={riskSupplementsListIsLoading}
            error={riskSupplementsListError && t('errors.highRiskSupplementsListError')}
            onRetry={onFetchEntriesList}
          >
            {Boolean(riskSupplementsList && riskSupplementsList.length) &&
              riskSupplementsList?.map(item => (
                <HRSItemCard
                  data={item}
                  isActive={item.id === activeEntryId}
                  onClick={onEntryClick}
                />
              ))}
            {Boolean(!riskSupplementsList || !riskSupplementsList.length) && <HRSListEmptyCard />}
          </Loadable>
        </Box>
      </Box>
      {!!listPagination?.pageCount && (
        <Pagination
          className="inquiry-list__pagination"
          color="primary"
          page={listPagination.page}
          count={listPagination.pageCount}
          onChange={handleSearchPagination}
        />
      )}
    </>
  );
};

export default HRSFullList;
